import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { TransportTypeService } from '@/shared/api/services'
import type {
  TransportTypeListResponse,
  TransportTypeCreateRequest,
  TransportTypeUpdateRequest,
  TransportTypeResponse
} from '@/shared/api/types'

// === TRANSPORT TYPES HOOKS ===

/**
 * Получить список видов транспорта с пагинацией и фильтрами
 */
export const useTransportTypesList = (params?: {
  page?: number
  limit?: number
  search?: string
  include_inactive?: boolean
}) => {
  return useQuery({
    queryKey: ['transportTypes', 'list', params],
    queryFn: () => TransportTypeService.getTransportTypesList(params),
    staleTime: 5 * 60 * 1000, // 5 минут
  })
}

/**
 * Создать новый вид транспорта
 */
export const useCreateTransportType = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (request: TransportTypeCreateRequest) =>
      TransportTypeService.createTransportType(request),
    onSuccess: (data: TransportTypeResponse) => {
      // Инвалидируем кэш списка
      queryClient.invalidateQueries({ queryKey: ['transportTypes', 'list'] })
      toast.success(`Вид транспорта "${data.name}" успешно создан`)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Ошибка при создании вида транспорта'
      if (!error?.alreadyToasted) {
        toast.error(message)
      }
    },
  })
}

/**
 * Обновить вид транспорта
 */
export const useUpdateTransportType = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: TransportTypeUpdateRequest }) =>
      TransportTypeService.updateTransportType(id, request),
    onSuccess: (data: TransportTypeResponse) => {
      // Инвалидируем кэш списка
      queryClient.invalidateQueries({ queryKey: ['transportTypes', 'list'] })
      toast.success(`Вид транспорта "${data.name}" успешно обновлен`)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Ошибка при обновлении вида транспорта'
      if (!error?.alreadyToasted) {
        toast.error(message)
      }
    },
  })
}

/**
 * Удалить вид транспорта
 */
export const useDeleteTransportType = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: number) => TransportTypeService.deleteTransportType(id),
    onSuccess: (data) => {
      // Инвалидируем кэш списка
      queryClient.invalidateQueries({ queryKey: ['transportTypes', 'list'] })
      toast.success(data.message)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Ошибка при удалении вида транспорта'
      if (!error?.alreadyToasted) {
        toast.error(message)
      }
    },
  })
}
